import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Code as CodeIcon,
  Palette as PaletteIcon,
  ContentCopy as CopyIcon,
  Description as DescriptionIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useFolderContext } from '../../context/FolderContext';
import { useAuth } from '../../context/AuthContext';

const FolderContextMenu = ({ 
  folder,
  contextMenu, 
  onClose
}) => {
  const { user } = useAuth();
  const { 
    updateFolder, 
    deleteFolder, 
    moveFolder, 
    createFolder,
    isRootFolder,
    getFolderActions,
    folderTree
  } = useFolderContext();

  const [renameDialog, setRenameDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [moveDialog, setMoveDialog] = useState(false);
  const [descriptionDialog, setDescriptionDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedParent, setSelectedParent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!folder || !contextMenu) return null;

  const isRoot = isRootFolder(folder.id);
  const folderActions = getFolderActions(folder.id);

  // Helper function to get all folders except current folder and its descendants
  const getAvailableParentFolders = (currentFolderId, folders = folderTree, result = []) => {
    for (const f of folders) {
      if (f.id !== currentFolderId) {
        result.push(f);
        if (f.children && f.children.length > 0) {
          getAvailableParentFolders(currentFolderId, f.children, result);
        }
      }
    }
    return result;
  };

  const handleMenuItemClick = (action) => {
    onClose();
    
    switch (action) {
      case 'rename':
        setNewName(folder.name);
        setRenameDialog(true);
        break;
      case 'editDesc':
        setNewDescription(folder.description || '');
        setDescriptionDialog(true);
        break;
      case 'addChild':
        // This will be handled by parent component
        break;
      case 'delete':
        setDeleteDialog(true);
        break;
      case 'move':
        setSelectedParent('');
        setMoveDialog(true);
        break;
      default:
        break;
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await updateFolder(folder.id, { name: newName.trim() });
      
      setRenameDialog(false);
      setNewName('');
    } catch (err) {
      setError(err.friendlyMessage || 'Failed to rename folder');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDescription = async () => {
    try {
      setLoading(true);
      setError('');
      
      await updateFolder(folder.id, { description: newDescription.trim() });
      
      setDescriptionDialog(false);
      setNewDescription('');
    } catch (err) {
      setError(err.friendlyMessage || 'Failed to update description');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError('');
      
      await deleteFolder(folder.id);
      
      setDeleteDialog(false);
    } catch (err) {
      setError(err.friendlyMessage || 'Failed to delete folder');
      setLoading(false);
    }
  };

  const handleMove = async () => {
    if (!selectedParent) {
      setError('Please select a parent folder');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await moveFolder(folder.id, selectedParent);
      
      setMoveDialog(false);
      setSelectedParent('');
    } catch (err) {
      setError(err.friendlyMessage || 'Failed to move folder');
    } finally {
      setLoading(false);
    }
  };

  const closeAllDialogs = () => {
    setRenameDialog(false);
    setDeleteDialog(false);
    setMoveDialog(false);
    setDescriptionDialog(false);
    setError('');
    setLoading(false);
  };

  return (
    <>
      {/* Context Menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {folderActions.map((action, index) => (
          <MenuItem
            key={action.action}
            onClick={() => handleMenuItemClick(action.action)}
            disabled={!action.enabled}
          >
            <ListItemIcon>
              {action.action === 'rename' && <EditIcon fontSize="small" />}
              {action.action === 'editDesc' && <DescriptionIcon fontSize="small" />}
              {action.action === 'addChild' && <CreateNewFolderIcon fontSize="small" />}
              {action.action === 'delete' && (
                action.enabled ? 
                  <DeleteIcon fontSize="small" /> : 
                  <BlockIcon fontSize="small" color="disabled" />
              )}
              {action.action === 'move' && (
                action.enabled ? 
                  <MoveIcon fontSize="small" /> : 
                  <BlockIcon fontSize="small" color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={action.label}
              primaryTypographyProps={{
                color: action.enabled ? 'inherit' : 'text.disabled'
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialog} onClose={closeAllDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Rename {isRoot ? 'Workspace' : 'Folder'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={isRoot ? "Workspace Name" : "Folder Name"}
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button 
            onClick={handleRename} 
            variant="contained"
            disabled={loading || !newName.trim()}
          >
            {loading ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={descriptionDialog} onClose={closeAllDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {isRoot ? 'Workspace' : 'Folder'} Description
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder={`Add a description for your ${isRoot ? 'workspace' : 'folder'}...`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button 
            onClick={handleUpdateDescription} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={closeAllDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete the folder "{folder.name}"?
          </Typography>
          {folder.children && folder.children.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This folder contains subfolders. All contents will be deleted.
            </Alert>
          )}
          {folder.snippetCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This folder contains {folder.snippetCount} snippet(s). All snippets will be moved to the root folder.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveDialog} onClose={closeAllDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Move Folder</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography sx={{ mb: 2 }}>
            Select a new parent folder for "{folder.name}":
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Parent Folder</InputLabel>
            <Select
              value={selectedParent}
              label="Parent Folder"
              onChange={(e) => setSelectedParent(e.target.value)}
            >
              {getAvailableParentFolders(folder.id).map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name} {isRootFolder(f.id) ? '(Workspace)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAllDialogs}>Cancel</Button>
          <Button 
            onClick={handleMove} 
            variant="contained"
            disabled={loading || !selectedParent}
          >
            {loading ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderContextMenu;
